import {
  CURRENCY_SYMBOLS,
  DEFAULT_CURRENCY_CODE,
  DEFAULT_CURRENCY_SYMBOL,
} from "../../constants/currencies.constants";
import { UNITS } from "../../constants/units.constant";

const DEFAULT_VOLUME_UNIT = UNITS.CUBIC_METER;

// increase the max range to 100000 to allow for large utility costs
// as wavepro does not have a limit on the utility costs
// THIS CHANGE IS TEMPORARY UNTIL WE GET A FINAL DECISION ON THE MAX RANGE FOR UTILITY COSTS
export const UTILITY_FIELDS = {
  priceOfWater: {
    unitType: "volume",
    // ranges: { hard: { min: 0, max: 10 } },
    ranges: { hard: { min: 0, max: 100000 } },
    defaultValue: "0.90",
  },
  costOfDowntime: {
    unitType: "time",
    ranges: { hard: { min: 0, max: 100000 } },
    defaultValue: "75",
  },
  costOfElectricity: {
    unitType: "energy",
    // ranges: { hard: { min: 0, max: 10 } },
    ranges: { hard: { min: 0, max: 100000 } },
    defaultValue: "0.10",
  },
};

export const FIELD_KEYS = Object.keys(UTILITY_FIELDS);
export const FIELD_ENTRIES = Object.entries(UTILITY_FIELDS);

const VOLUME_CONVERSION_FACTOR_M3_TO_KGAL = 0.26417205;

const normalizeVolumeUnit = (volume = DEFAULT_VOLUME_UNIT) => {
  const normalized = String(volume || DEFAULT_VOLUME_UNIT).toLowerCase();

  if (normalized === "m3" || normalized === "m³") {
    return "m3";
  }

  if (normalized === "kgal") {
    return "kgal";
  }

  return normalized;
};

const normalizeConversionRate = (currency, conversionRate) => {
  if (currency === DEFAULT_CURRENCY_CODE) {
    return 1;
  }

  const parsedRate = Number(conversionRate);
  return Number.isFinite(parsedRate) && parsedRate > 0 ? parsedRate : 1;
};

const convertCurrencyValue = (
  value,
  fromCurrency,
  fromRate,
  toCurrency,
  toRate,
) => {
  let convertedValue = Number(value);

  if (!Number.isFinite(convertedValue)) {
    return 0;
  }

  if (fromCurrency !== DEFAULT_CURRENCY_CODE) {
    convertedValue /= normalizeConversionRate(fromCurrency, fromRate);
  }

  if (toCurrency !== DEFAULT_CURRENCY_CODE) {
    convertedValue *= normalizeConversionRate(toCurrency, toRate);
  }

  return convertedValue;
};

const convertVolumeValue = (value, fromVolume, toVolume) => {
  const normalizedFromVolume = normalizeVolumeUnit(fromVolume);
  const normalizedToVolume = normalizeVolumeUnit(toVolume);

  if (normalizedFromVolume === normalizedToVolume) {
    return value;
  }

  if (normalizedFromVolume === "m3" && normalizedToVolume === "kgal") {
    return value * VOLUME_CONVERSION_FACTOR_M3_TO_KGAL;
  }

  if (normalizedFromVolume === "kgal" && normalizedToVolume === "m3") {
    return value / VOLUME_CONVERSION_FACTOR_M3_TO_KGAL;
  }

  return value;
};

const convertVolumeRateValue = (value, fromVolume, toVolume) => {
  // For currency/volume, denominator conversion is inverse.
  const volumeFactor = convertVolumeValue(1, fromVolume, toVolume);

  return value / volumeFactor;
};

const getUnitByType = (unitType, volume) => {
  const units = {
    volume: volume || DEFAULT_VOLUME_UNIT,
    time: UNITS.day,
    energy: UNITS.kiloWattHour,
  };

  return units[unitType] || "";
};

export const getFieldSuffix = (field, currency, volume) => {
  const currencySymbol = CURRENCY_SYMBOLS[currency] || DEFAULT_CURRENCY_SYMBOL;
  const unit = getUnitByType(field.unitType, volume);

  return unit ? `${currencySymbol}/${unit}` : "";
};

export const getInitialValues = () =>
  Object.fromEntries(
    FIELD_ENTRIES.map(([key, field]) => [key, field.defaultValue]),
  );

export const convertUtilityFormValues = (
  values = {},
  fromUnits = {},
  toUnits = {},
) => {
  return FIELD_KEYS.reduce((acc, fieldKey) => {
    const rawValue = values[fieldKey];

    if (rawValue === "" || rawValue === null || rawValue === undefined) {
      acc[fieldKey] = rawValue;
      return acc;
    }

    const field = UTILITY_FIELDS[fieldKey];
    const numericValue = Number(rawValue);

    if (!Number.isFinite(numericValue)) {
      acc[fieldKey] = rawValue;
      return acc;
    }

    let convertedValue = numericValue;

    if (field?.unitType === "volume") {
      convertedValue = convertVolumeRateValue(
        convertedValue,
        fromUnits?.volume,
        toUnits?.volume,
      );
    }

    convertedValue = convertCurrencyValue(
      convertedValue,
      fromUnits?.currency,
      fromUnits?.conversion_rate,
      toUnits?.currency,
      toUnits?.conversion_rate,
    );

    acc[fieldKey] = Number.isFinite(convertedValue)
      ? String(convertedValue)
      : String(rawValue);

    return acc;
  }, {});
};

const convertUtilityRangeValue = (
  value,
  field,
  fromUnits = {},
  toUnits = {},
) => {
  let convertedValue = Number(value);

  if (!Number.isFinite(convertedValue)) {
    return value;
  }

  if (field?.unitType === "volume") {
    convertedValue = convertVolumeRateValue(
      convertedValue,
      fromUnits?.volume,
      toUnits?.volume,
    );
  }

  convertedValue = convertCurrencyValue(
    convertedValue,
    fromUnits?.currency,
    fromUnits?.conversion_rate,
    toUnits?.currency,
    toUnits?.conversion_rate,
  );

  return Number.isFinite(convertedValue)
    ? Number(convertedValue.toFixed(2))
    : value;
};

export const getUtilityFieldRanges = (
  currency = DEFAULT_CURRENCY_CODE,
  volume = DEFAULT_VOLUME_UNIT,
  conversionRate = 1,
) => {
  const baseUnits = {
    currency: DEFAULT_CURRENCY_CODE,
    volume: DEFAULT_VOLUME_UNIT,
    conversion_rate: 1,
  };

  const targetUnits = {
    currency,
    volume,
    conversion_rate: conversionRate,
  };

  return FIELD_ENTRIES.reduce((acc, [fieldKey, field]) => {
    const hardMin = field?.ranges?.hard?.min;
    const hardMax = field?.ranges?.hard?.max;

    acc[fieldKey] = {
      hard: {
        min: convertUtilityRangeValue(hardMin, field, baseUnits, targetUnits),
        max: convertUtilityRangeValue(hardMax, field, baseUnits, targetUnits),
      },
    };

    return acc;
  }, {});
};
